// src/pages/Account.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import {
  BuildingOfficeIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Import section components
import BusinessDetails from '../components/account/BusinessDetails';
import SecuritySettings from '../components/account/SecuritySettings';

const Account = () => {
  const { currentUser, jeweller } = useAuth();
  const [activeTab, setActiveTab] = useState('business');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accountData, setAccountData] = useState(null);
  const [alertInfo, setAlertInfo] = useState({ open: false, type: 'success', message: '' });

  // Fetch jeweller data from Firestore
  useEffect(() => {
    const fetchAccountData = async () => {
      if (!currentUser?.uid) return;
      
      try {
        const docRef = doc(db, 'jewellers', currentUser.uid);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setAccountData({
            // Business Information
            registeredCompanyName: data.registeredCompanyName || data.businessName || '',
            companyRegistrationNumber: data.companyRegistrationNumber || '',
            primaryContactName: data.primaryContactName || '',
            primaryContactEmail: data.primaryContactEmail || data.email || '',
            primaryContactPhone: data.primaryContactPhone || '',
            registeredBusinessAddress: {
              line1: data.registeredBusinessAddress?.line1 || '',
              line2: data.registeredBusinessAddress?.line2 || '',
              city: data.registeredBusinessAddress?.city || '',
              county: data.registeredBusinessAddress?.county || '',
              postcode: data.registeredBusinessAddress?.postcode || '',
              country: data.registeredBusinessAddress?.country || 'United Kingdom'
            },
            
            // Financial Details
            vatNumber: data.vatNumber || '',
            vatRegistered: data.vatRegistered || false,
            
            // Security
            loginEmail: currentUser.email || '',
            
            // Metadata
            ...data
          });
        } else {
          // Initialize with empty data
          setAccountData({
            registeredCompanyName: '',
            companyRegistrationNumber: '',
            primaryContactName: '',
            primaryContactEmail: currentUser.email || '',
            primaryContactPhone: '',
            registeredBusinessAddress: {
              line1: '',
              line2: '',
              city: '',
              county: '',
              postcode: '',
              country: 'United Kingdom'
            },
            vatNumber: '',
            vatRegistered: false,
            loginEmail: currentUser.email || ''
          });
        }
      } catch (error) {
        console.error('Error fetching account data:', error);
        showAlert('error', 'Failed to load account data');
      } finally {
        setLoading(false);
      }
    };

    fetchAccountData();
  }, [currentUser]);

  const showAlert = (type, message) => {
    setAlertInfo({ open: true, type, message });
    setTimeout(() => setAlertInfo({ open: false, type: '', message: '' }), 5000);
  };

  const handleSave = async (section, data) => {
    if (!currentUser?.uid) return;
    
    setSaving(true);
    try {
      const docRef = doc(db, 'jewellers', currentUser.uid);
      const updateData = {
        ...data,
        lastUpdated: Timestamp.now(),
        updatedBy: currentUser.uid
      };
      
      await updateDoc(docRef, updateData);
      
      // Update local state
      setAccountData(prev => ({ ...prev, ...data }));
      
      showAlert('success', `${section} updated successfully`);
    } catch (error) {
      console.error('Error saving account data:', error);
      showAlert('error', `Failed to update ${section}`);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    {
      value: 'business',
      label: 'Business Details',
      icon: BuildingOfficeIcon,
      component: BusinessDetails
    },
    {
      value: 'security',
      label: 'Security',
      icon: ShieldCheckIcon,
      component: SecuritySettings
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      {/* Alert */}
      {alertInfo.open && (
        <div
          className={`
            fixed top-5 right-5 z-[10001] flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg
            ${alertInfo.type === 'success'
              ? 'bg-green-50 border-l-4 border-green-500 text-green-700'
              : 'bg-red-50 border-l-4 border-red-500 text-red-700'}
          `}
        >
          {alertInfo.type === 'success' 
            ? <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
            : <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          }
          <span className="text-sm font-medium">{alertInfo.message}</span>
          <button
            onClick={() => setAlertInfo({ ...alertInfo, open: false })}
            className="ml-auto text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Account Settings
        </h1>
        <p className="text-gray-600">
          Manage your business information and security settings
        </p>
      </div>

      {/* Tabs */}
      <div className="card">
        {/* Tab Headers */}
        <div className="flex border-b border-gray-200 -m-6 mb-6">
          {tabs.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              onClick={() => setActiveTab(value)}
              className={`
                flex items-center gap-2 px-6 py-4 font-medium transition-all relative
                ${activeTab === value 
                  ? 'text-purple-600 border-b-2 border-purple-500' 
                  : 'text-gray-600 hover:text-gray-900'}
              `}
            >
              <Icon className="h-5 w-5" />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {tabs.map(({ value, component: Component }) => (
            <div
              key={value}
              className={activeTab === value ? 'block' : 'hidden'}
            >
              <Component
                data={accountData}
                onSave={(data) => handleSave(value, data)}
                saving={saving}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Account;